// Connected services' overview: the services that send Origin activity, each
// with its status and the ones needing attention first, then the services not
// set up yet as tiles to add. A service opens as its own page in the group
// (SettingsGroup.jsx), which names it and says how it is doing.
import { phAttrs } from "../../lib/analytics/attrs.js";
import { ATTRIBUTE } from "../../lib/analytics/surfaceVocabulary.js";
import { sourceByKey } from "../../pages/dataSources/model.js";
import { SourceLogo, StatusPill, TONE } from "../statusCard.jsx";
import Chevron from "../ui/Chevron.jsx";
import { PAGE_PART } from "./Page.jsx";

// Problems first, then services still waiting on someone, then the working ones.
const ORDER = [TONE.ATTENTION, TONE.UNAVAILABLE, TONE.WAITING, TONE.LOADING, TONE.OK];

function ServiceRow({ section, status, onOpen }) {
    return (
        <button
            type="button"
            data-settings-section={section.address}
            onClick={() => onOpen(section.address)}
            {...phAttrs({ [ATTRIBUTE.SETTING]: section.address })}
            className="grid w-full cursor-pointer grid-cols-[34px_minmax(0,1fr)_auto_12px] items-center gap-[14px] rounded-[6px] border-0 bg-transparent px-[8px] py-[10px] text-left transition-colors hover:bg-[color-mix(in_srgb,var(--ink)_7%,transparent)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[color:var(--green)]"
        >
            <span className="grid h-[34px] w-[34px] place-items-center rounded-[7px] bg-[color-mix(in_srgb,var(--ink)_8%,transparent)]">
                <SourceLogo logo={sourceByKey(section.id)?.logo} size={20} />
            </span>
            <span className="min-w-0 text-[14px] font-medium text-[color:var(--ink)]">{section.name}</span>
            {status ? <StatusPill tone={status.tone} label={status.label} /> : <span />}
            <span className="text-[color:var(--muted-soft)]"><Chevron /></span>
        </button>
    );
}

function AddTile({ section, onOpen }) {
    return (
        <button
            type="button"
            data-settings-section={section.address}
            onClick={() => onOpen(section.address)}
            {...phAttrs({ [ATTRIBUTE.SETTING]: section.address })}
            className="flex h-[58px] cursor-pointer items-center gap-[12px] rounded-[10px] border border-dashed border-[color:var(--line-strong)] bg-transparent px-[14px] text-left transition-colors hover:border-[color:var(--ink)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[color:var(--green)]"
        >
            <span className="grid h-[30px] w-[30px] shrink-0 place-items-center rounded-[7px] bg-[color-mix(in_srgb,var(--ink)_8%,transparent)]">
                <SourceLogo logo={sourceByKey(section.id)?.logo} size={18} />
            </span>
            <span className="min-w-0 flex-1 text-[13.5px] font-medium text-[color:var(--ink)]">{section.name}</span>
            <svg viewBox="0 0 14 14" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" className="h-[13px] w-[13px] shrink-0 text-[color:var(--muted)]">
                <path d="M7 2v10M2 7h10" />
            </svg>
        </button>
    );
}

// `statuses` is each section's { tone, label } once the services have been
// read; until then every service is listed, without a status.
export default function ServicesOverview({ group, onOpen, statuses = null }) {
    const status = (section) => statuses?.[section.id] ?? null;
    const rank = (section) => {
        const i = ORDER.indexOf(status(section)?.tone);
        return i < 0 ? ORDER.length : i;
    };
    const connected = statuses
        ? group.sections.filter((s) => status(s) && status(s).tone !== TONE.SETUP).sort((a, b) => rank(a) - rank(b))
        : group.sections;
    const toAdd = statuses ? group.sections.filter((s) => status(s)?.tone === TONE.SETUP) : [];

    return (
        <div className="grid min-w-0 gap-[26px]" aria-busy={statuses ? undefined : "true"}>
            {connected.length ? (
                <div className="grid gap-[4px]" data-services-part="connected">
                    {statuses ? <h3 className={`${PAGE_PART} pb-[6px]`}>Connected <span className="font-normal tabular-nums text-[color:var(--muted)]">{connected.length}</span></h3> : null}
                    {connected.map((section) => <ServiceRow key={section.id} section={section} status={status(section)} onOpen={onOpen} />)}
                </div>
            ) : null}
            {toAdd.length ? (
                <div className="grid gap-[10px]" data-services-part="add">
                    <h3 className={PAGE_PART}>Add a service</h3>
                    <div className="grid grid-cols-3 gap-[10px] max-[999px]:grid-cols-2 max-[519px]:grid-cols-1">
                        {toAdd.map((section) => <AddTile key={section.id} section={section} onOpen={onOpen} />)}
                    </div>
                </div>
            ) : null}
        </div>
    );
}
